import React, { Suspense, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, Route, Routes } from 'react-router-dom';
import {
  ClerkLoaded,
  ClerkLoading,
  AuthenticateWithRedirectCallback,
  useAuth
} from '@clerk/clerk-react';
import { useLocation, useNavigate } from 'react-router-dom';
import AuthGuard from '@/components/auth/AuthGuard';
import OneSignalConfig from '@/components/oneSIgnalConfig';
import ContactUs from './components/ContactUs';
import TimModal from '@/tim';
import { useTimBackgroundInit } from '@/tim/useTimBackgroundInit';
import { Loading } from 'aha-components';
import { initCustomEvent, Tracker } from './utils';
import {
  isMobile,
  isFromApp,
  getKeyFromUrlSearch,
  setStoredLeaderboardShareId,
  setStoredReferralCodeIfEmpty
} from '@/biz/tool';
import EmailUnsubscribe from './pages/EmailUnsubscribe';
import EmailConfirmed from './pages/EmailConfirmed';
import DownloadAppBanner from './components/DownloadAppBanner';
import { parseTimOpenSource, useTimModalStore } from '@/tim/useTimModalStore';
import AppAuthProgress from './app/auth/AppAuthProgress';
import { clearAppAuthLoading } from '@/app/auth/loading';
import type { AppAuthProvider } from './app/auth/appAuthExternal';
import NativeBridgeUpgradePage from '@/app/bridge/NativeBridgeUpgradePage';
import {
  NATIVE_BRIDGE_UPGRADE_PATH,
  shouldForceNativeBridgeUpgrade
} from '@/app/bridge/ahaBridge';

// 导入样式
import './index.css';
import NewNavbar from './components/NewNavbar';
import LowerFreeBanner, {
  shouldShowLowerFreeBanner
} from './components/LowerFreeBanner';
import ReferralCodeGate from './components/referral/ReferralCodeGate';

// 懒加载页面组件
const Home = React.lazy(
  () => import(/* webpackChunkName: "page~components~Home" */ './pages/home')
);

const ClerkCallbackPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "page~login~clerkCallbackPage" */ './pages/login/clerkCallbackPage'
    )
);

/**
 * /clerk 路由包装：必须先由 AuthenticateWithRedirectCallback 处理 OAuth 回调并建立 session，
 * 否则 OAuth 授权回调后 session 未建立，会变成未登录并跳到 /login。
 */
const ClerkCallbackRoute: React.FC = () => {
  const { pathname, search } = useLocation();
  const { isSignedIn } = useAuth();
  const fallbackUrl = pathname + (search || '');

  return (
    <>
      {!isSignedIn ? (
        <AuthenticateWithRedirectCallback
          signInFallbackRedirectUrl={fallbackUrl}
          signUpFallbackRedirectUrl={fallbackUrl}
        />
      ) : null}
      {isSignedIn ? <ClerkCallbackPage /> : null}
    </>
  );
};
const LoginPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "page~login~LoginPage" */ './pages/login/LoginPage'
    )
);
const SignupPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "page~login~SignupPage" */ './pages/login/SignupPage'
    )
);
const ForgotPasswordPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "page~login~ForgotPasswordPage" */ '@/pages/login/ForgotPasswordPage'
    )
);
const ExternalBrowserAuthBridgePage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "page~login~ExternalBrowserAuthBridgePage" */ './app/auth/ExternalBrowserAuthBridgePage'
    )
);
const AppAuthStartPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "page~login~AppAuthStartPage" */ './app/auth/AppAuthStartPage'
    )
);
const AppAuthCallbackPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "page~login~AppAuthCallbackPage" */ './app/auth/AppAuthCallbackPage'
    )
);

const AppAuthRouteLoading: React.FC<{
  pathname: string;
}> = ({ pathname }) => {
  if (pathname.startsWith('/app-auth/google/')) {
    return <AppAuthProgress tone="google" />;
  }
  if (pathname.startsWith('/app-auth/apple/')) {
    return <AppAuthProgress tone="apple" />;
  }
  if (pathname.startsWith('/app-auth/') || pathname === '/auth/bridge') {
    return <AppAuthProgress />;
  }
  return null;
};

const AppAuthCallbackRoute: React.FC<{
  provider?: AppAuthProvider;
}> = ({ provider = 'google' }) => {
  const { pathname, search } = useLocation();
  const { isSignedIn } = useAuth();
  const fallbackUrl = pathname + (search || '');

  return (
    <>
      {!isSignedIn ? (
        <>
          <AuthenticateWithRedirectCallback
            signInFallbackRedirectUrl={fallbackUrl}
            signUpFallbackRedirectUrl={fallbackUrl}
          />
          <AppAuthProgress tone={provider} />
        </>
      ) : null}
      {isSignedIn ? <AppAuthCallbackPage provider={provider} /> : null}
    </>
  );
};

const PlatformCallbackPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "page~login~PlatformCallbackPage" */ './pages/login/PlatformCallbackPage'
    )
);
const PlatformCallbackSassPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "page~login~PlatformCallbackSassPage" */ './pages/login/PlatformCallbackSassPage'
    )
);
const ConnectError = React.lazy(
  () =>
    import(
      /* webpackChunkName: "page~campaign~connectError" */ './pages/campaign/connectError'
    )
);
const ConnectSuccess = React.lazy(
  () =>
    import(
      /* webpackChunkName: "page~campaign~connectSuccess" */ './pages/campaign/connectSuccess'
    )
);
const ErrorPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "page~campaign~error" */ './pages/campaign/error'
    )
);
const MatchPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "page~campaign~match" */ './pages/campaign/match'
    )
);
const ConnectPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "page~campaign~connect" */ './pages/campaign/connect'
    )
);
const Wallet = React.lazy(
  () =>
    import(
      /* webpackChunkName: "page~campaign~wallet" */ './pages/campaign/wallet'
    )
);
const WalletPaymentMethod = React.lazy(
  () =>
    import(
      /* webpackChunkName: "page~campaign~walletPaymentMethod" */ './pages/campaign/walletPaymentMethod'
    )
);
const InvitesHome = React.lazy(
  () =>
    import(
      /* webpackChunkName: "page~campaign~invites" */ './pages/campaign/invites'
    )
);
const DetailNew = React.lazy(
  () =>
    import(
      /* webpackChunkName: "page~campaign~detailNew" */ './pages/campaign/detailNew'
    )
);
const ContactInfo = React.lazy(
  () => import(/* webpackChunkName: "page~contactInfo" */ './pages/contactInfo')
);
const Settings = React.lazy(
  () => import(/* webpackChunkName: "page~settings" */ './pages/settings')
);
const IconsPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "page~settings~icons" */ './pages/settings/icons'
    )
);

const Reviews = React.lazy(
  () => import(/* webpackChunkName: "page~settings~icons" */ './pages/Reviews')
);
// referral
const ReferralHome = React.lazy(
  () =>
    import(
      /* webpackChunkName: "page~referral~ReferralHome" */ './pages/Referral'
    )
);
const Leaderboard = React.lazy(
  () =>
    import(
      /* webpackChunkName: "page~leaderboard~Leaderboard" */ './pages/Leaderboard'
    )
);
const DeletePage = React.lazy(
  () => import(/* webpackChunkName: "page~delete" */ './pages/delete')
);
const DownloadAppPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "page~download~DownloadApp" */ './pages/DownloadApp'
    )
);
// onboarding
const OnboardingHome = React.lazy(
  () =>
    import(
      /* webpackChunkName: "page~onboarding~OnboardingHome" */ './pages/Onboarding'
    )
);
const ConnectSocials = React.lazy(
  () =>
    import(
      /* webpackChunkName: "page~onboarding~ConnectSocials" */ './pages/Onboarding/ConnectSocials'
    )
);

const DealsActivePage = React.lazy(
  () =>
    import(/* webpackChunkName: "page~deals~active" */ './pages/Deals/Active')
);
const DealsCompletedPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "page~deals~completed" */ './pages/Deals/Completed'
    )
);

const App: React.FC = () => {
  const router = useLocation();
  const navigate = useNavigate();
  const { isLoaded, isSignedIn } = useAuth();
  const { t } = useTranslation();
  const { showTimModal, timLoaded, setShowTimModal } = useTimModalStore();
  useTimBackgroundInit();
  const forceNativeBridgeUpgrade = shouldForceNativeBridgeUpgrade();
  const hideNavbar =
    forceNativeBridgeUpgrade ||
    router.pathname === NATIVE_BRIDGE_UPGRADE_PATH ||
    router.pathname.startsWith('/email-unsubscribe') ||
    router.pathname.startsWith('/email-confirmed') ||
    router.pathname.startsWith('/delete') ||
    router.pathname.startsWith('/download') ||
    router.pathname.startsWith('/app-auth/') ||
    router.pathname === '/auth/bridge' ||
    router.pathname === '/clerk' ||
    router.pathname === '/login' ||
    router.pathname === '/signup' ||
    router.pathname === '/forgot-password' ||
    router.pathname === '/onboarding' ||
    router.pathname === '/onboarding/connect-socials';
  const isHomePage = router.pathname === '/';
  const guestInviteId = isHomePage
    ? new URLSearchParams(router.search).get('id')?.trim()
    : '';
  const isGuestHome = isLoaded && !isSignedIn && Boolean(guestInviteId);
  const [showLowerFreeBanner, setShowLowerFreeBanner] = useState(
    shouldShowLowerFreeBanner
  );

  useEffect(() => {
    initCustomEvent();
  }, [router]);

  useEffect(() => {
    if (!forceNativeBridgeUpgrade) return;
    if (router.pathname === NATIVE_BRIDGE_UPGRADE_PATH) return;
    navigate(NATIVE_BRIDGE_UPGRADE_PATH, { replace: true });
  }, [forceNativeBridgeUpgrade, router.pathname, navigate]);

  useEffect(() => {
    if (!isFromApp() || !isLoaded) return;
    if (
      // HomePage closes native loading after dashboard data resolves; auth
      // bridge and Clerk callback routes keep it while the handoff is running.
      router.pathname === '/' ||
      router.pathname.startsWith('/app-auth/') ||
      router.pathname === '/auth/bridge' ||
      router.pathname === '/clerk'
    ) {
      return;
    }

    const timer = window.setTimeout(() => {
      clearAppAuthLoading();
    }, 100);
    return () => window.clearTimeout(timer);
  }, [isLoaded, router.pathname]);

  // 邀请码：仅首次生效，URL 携带 REFERRALCODE 时写入 storage，后续不同值不覆盖
  useEffect(() => {
    const code = getKeyFromUrlSearch('REFERRALCODE');
    if (code) setStoredReferralCodeIfEmpty(code);
    const leaderboardShareId = getKeyFromUrlSearch('leaderboardShareId');
    if (leaderboardShareId) setStoredLeaderboardShareId(leaderboardShareId);
  }, [router.search]);

  useEffect(() => {
    if (!isLoaded || !isSignedIn) return;
    const query = new URLSearchParams(router.search);
    if (query.get('tim') !== '1') return;

    const source = parseTimOpenSource(query.get('source'));
    setShowTimModal(true, { source });
  }, [
    isLoaded,
    isSignedIn,
    router.pathname,
    router.search,
    router.hash,
    navigate,
    setShowTimModal
  ]);

  useEffect(() => {
    if (!isLoaded || !isSignedIn || !showTimModal || !timLoaded) return;

    const query = new URLSearchParams(router.search);
    if (query.get('tim') !== '1') return;

    query.delete('tim');
    query.delete('source');

    navigate(
      {
        pathname: router.pathname,
        search: query.toString() ? `?${query.toString()}` : '',
        hash: router.hash
      },
      { replace: true }
    );
  }, [
    isLoaded,
    isSignedIn,
    showTimModal,
    timLoaded,
    router.pathname,
    router.search,
    router.hash,
    navigate
  ]);

  // 处理 DM 短链接重定向: 转换为完整的 campaign detail URL
  useEffect(() => {
    if (router.pathname === '/') {
      const query = new URLSearchParams(router.search);
      const igOrderId = query.get('ig');

      if (igOrderId) {
        // 对应 https://xxx.inc?ig=$orderId
        // 上报 DM 短链接访问事件
        try {
          Tracker.gtag('event', 'screen_view', {
            event_category: 'dm_short_link_invitation_visit',
            utm_source: 'ig',
            utm_medium: 'dm',
            utm_campaign: 'invitation_campaign',
            order_id: igOrderId,
            value: 1
          });
        } catch (error) {
          console.error(
            '❌ Error sending dm_short_link_invitation_visit event:',
            error
          );
        }
        // 将短链接转换为完整的 campaign detail URL，并带上 UTM 参数
        // 新增处理，支持进入聚合链接，根据 ID 长度
        const fullUrl =
          igOrderId.length > 20
            ? `/campaign/detail/${igOrderId}?utm_source=ig&utm_medium=dm&utm_campaign=invitation_campaign`
            : `/campaign/invites?id=${igOrderId}&utm_source=ig&utm_medium=dm&utm_campaign=invitation_campaign`;

        navigate(fullUrl, { replace: true });
      } else if (igOrderId === '') {
        // 对应邀约注册 https://xxx.inc?ig
        // 上报 DM 邀约注册
        try {
          Tracker.gtag('event', 'screen_view', {
            event_category: 'dm_short_link_signup_visit',
            utm_source: 'ig',
            utm_medium: 'dm'
          });
        } catch (error) {
          console.error(
            '❌ Error sending dm_short_link_signup_visit event:',
            error
          );
        }
      }
      // TODO(pengrui): 后续扩展其他平台
    }
  }, [router.pathname, router.search, navigate]);

  if (
    forceNativeBridgeUpgrade ||
    router.pathname === NATIVE_BRIDGE_UPGRADE_PATH
  ) {
    return <NativeBridgeUpgradePage />;
  }

  const isDownloadLanding =
    router.pathname === '/download' || router.pathname.startsWith('/download/');

  if (isDownloadLanding) {
    return (
      <div id="app" className="min-h-screen w-full">
        <Suspense
          fallback={
            <div className="flex flex-col justify-center items-center min-h-screen w-full">
              <Loading width={50} height={50} color="#000" />
            </div>
          }
        >
          <DownloadAppPage />
        </Suspense>
      </div>
    );
  }

  return (
    <div
      id="app"
      className={`${
        isMobile() ||
        router.pathname.startsWith('/app-auth/') ||
        router.pathname === '/auth/bridge'
          ? ''
          : 'webAppContainer'
      }
     ${hideNavbar ? '' : 'flex flex-col md:flex-row w-full'}`}
    >
      {/* OneSignal 先初始化点击监听；权限请求延后到登录完成后，避免打断登录链路 */}
      <OneSignalConfig />
      <ClerkLoaded>
        <AuthGuard>
          <ReferralCodeGate>
            {!hideNavbar && <NewNavbar guestMode={isGuestHome} />}
            {/* 新版主内容区占满侧栏外剩余空间并 min-h-0，否则子页 MobileListRefresh 的 h-full 无高度基准，移动端滚动易落在 window，全局「回到顶部」失效 */}
            <div className="flex flex-1 flex-col min-h-0 w-full min-w-0">
              <div className="flex flex-1 flex-col min-h-0 min-w-0 overflow-hidden">
                <Suspense
                  fallback={<AppAuthRouteLoading pathname={router.pathname} />}
                >
                  <Routes>
                    <Route path="/login" element={<LoginPage />} />
                    <Route path="/signup" element={<SignupPage />} />
                    <Route
                      path="/forgot-password"
                      element={<ForgotPasswordPage />}
                    />
                    <Route
                      path="/app-auth/google/start"
                      element={<AppAuthStartPage provider="google" />}
                    />
                    <Route
                      path="/app-auth/google/callback"
                      element={<AppAuthCallbackRoute provider="google" />}
                    />
                    <Route
                      path="/app-auth/apple/start"
                      element={<AppAuthStartPage provider="apple" />}
                    />
                    <Route
                      path="/app-auth/apple/callback"
                      element={<AppAuthCallbackRoute provider="apple" />}
                    />
                    <Route
                      path="/auth/bridge"
                      element={<ExternalBrowserAuthBridgePage />}
                    />
                    <Route path="/delete" element={<DeletePage />} />
                    <Route path="/" element={<Home />} />
                    {/* <Route path="/web" element={<Web />} /> */}
                    <Route path="/campaign/error" element={<ErrorPage />} />
                    <Route path="/match" element={<MatchPage />} />
                    <Route path="/connect" element={<ConnectPage />} />
                    <Route path="/contactInfo" element={<ContactInfo />} />
                    <Route
                      path="/auth/:platform/callback"
                      element={<PlatformCallbackPage />}
                    />
                    <Route
                      path="/auth/:platform/"
                      element={<PlatformCallbackSassPage />}
                    />
                    <Route path="/clerk" element={<ClerkCallbackRoute />} />
                    <Route
                      path="/auth/:platform/connectError"
                      element={<ConnectError />}
                    />
                    <Route
                      path="/auth/:platform/success"
                      element={<ConnectSuccess />}
                    />
                    {/*  */}
                    {/* 以下是的新页面 */}
                    {/*  */}
                    <Route
                      path="/campaign/detail/:orderId"
                      element={<DetailNew />}
                    />
                    <Route
                      path="/campaign/invites/detail/:orderId"
                      element={<DetailNew />}
                    />
                    <Route
                      path="/campaign/deals/detail/:orderId"
                      element={<DetailNew />}
                    />
                    <Route
                      path="/campaign/wallet/:orderId?"
                      element={<Wallet />}
                    />
                    <Route
                      path="/campaign/wallet/payment/method/update/:type"
                      element={<WalletPaymentMethod />}
                    />
                    <Route path="/campaign/invites" element={<InvitesHome />} />
                    <Route
                      path="/campaign/deals"
                      element={<Navigate to="/deals/active" />}
                    />
                    {/*  */}
                    <Route path="/settings" element={<Settings />} />
                    {/* referral */}
                    <Route path="/referral" element={<ReferralHome />} />
                    <Route path="/leaderboard" element={<Leaderboard />} />
                    <Route
                      path="/leaderboard/report"
                      element={<Leaderboard requireViewer />}
                    />
                    {/* 评论 */}
                    <Route path="/reviews" element={<Reviews />} />
                    {/* Brand deals */}
                    <Route
                      path="/deals/opportunities"
                      element={
                        <Navigate
                          to={{ pathname: '/', search: router.search }}
                          replace
                        />
                      }
                    />
                    <Route path="/deals/active" element={<DealsActivePage />} />
                    <Route
                      path="/deals/completed"
                      element={<DealsCompletedPage />}
                    />
                    {/* onboarding 首页 */}
                    <Route path="/onboarding" element={<OnboardingHome />} />
                    <Route
                      path="/onboarding/connect-socials"
                      element={<ConnectSocials />}
                    />
                    {process.env.REACT_APP_ENV !== 'production' && (
                      <Route path="/icons" element={<IconsPage />} />
                    )}
                    {/*  */}
                    <Route path="*" element={<ErrorPage />} />
                    <Route
                      path="/email-unsubscribe"
                      element={<EmailUnsubscribe />}
                    />
                    <Route
                      path="/email-confirmed"
                      element={<EmailConfirmed />}
                    />
                  </Routes>
                </Suspense>
              </div>
            </div>
            <TimModal />
          </ReferralCodeGate>
        </AuthGuard>
      </ClerkLoaded>
      <ClerkLoading>
        {router.pathname.startsWith('/app-auth/') ||
        router.pathname === '/auth/bridge' ? (
          <AppAuthRouteLoading pathname={router.pathname} />
        ) : (
          <div className="flex flex-col justify-center items-center h-[calc(60vh)] w-full">
            <Loading width={50} height={50} color="#000" />
            <div className="text-gray-500 text-base">{t('common.loading')}</div>
          </div>
        )}
      </ClerkLoading>
      {!router.pathname.startsWith('/download') && <ContactUs />}
    </div>
  );
};

export default App;
